import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

type Overview = { title?: string; url?: string }

/**
 * Global CMS data (overview links, site info, seo, navigation, translations). The actions push the
 * fetched navigation into the `navigation` store.
 */
export const useGlobalStore = defineStore('global', () => {
  const cacheStore = useCacheStore()
  const navigationStore = useNavigationStore()
  const cmsFetch = useCmsFetch()
  const { locale } = useI18n()

  const discoveryOverview = ref<Overview>({ title: undefined, url: undefined })
  const discoverySearchOverview = ref<Overview>({ title: undefined, url: undefined })
  const errorPages = ref<any[]>([])
  const eventOverview = ref<Overview>({ title: undefined, url: undefined })
  const favoritePage = ref<Overview>({ title: undefined, url: undefined })
  const pressOverview = ref<Overview>({ title: undefined, url: undefined })
  const search = ref<Overview>({ title: undefined, url: undefined })
  const seriesOverview = ref<Overview>({ title: undefined, url: undefined })
  const site = ref<{ handle?: string; language: string; name?: string }>({ handle: undefined, language: 'nl', name: undefined })
  const translations = ref<Record<string, unknown>>({})
  const vriendenloterij = ref<{ introduction?: string }>({ introduction: undefined })

  async function fetchGlobal() {
    const fetcher = async () => await cmsFetch(`/api/default/${locale.value}/global.json`)
    const data = await cacheStore.fetch({ key: `global;locale:${locale.value}`, ttl: 3600, fetcher })
    if (data) {
      discoveryOverview.value = data.discoveryOverview
      discoverySearchOverview.value = data.discoverySearchOverview
      errorPages.value = data.errorPages
      eventOverview.value = data.eventOverview
      favoritePage.value = data.favoritePage
      pressOverview.value = data.pressOverview
      search.value = data.search
      seriesOverview.value = data.seriesOverview
      site.value = data.site
      translations.value = data.translations
      vriendenloterij.value = data.vriendenloterij
    }
    return data
  }

  async function fetchNavigation() {
    const data = await fetchGlobal()
    if (data) {
      navigationStore.setNavigation(data.navigation)
    }
    return data?.navigation
  }

  async function fetchSeo() {
    const data = await fetchGlobal()
    return data?.seo
  }

  async function fetchErrorPage({ statusCode }: { statusCode: number }) {
    const data = await fetchGlobal()
    // Return the page with the matching statusCode, else the fallback page.
    for (const page of data?.errorPages || []) {
      if (page.statusCode === statusCode) {
        return page
      }
    }
    for (const page of data?.errorPages || []) {
      if (page.isFallback) {
        return page
      }
    }
  }

  return {
    discoveryOverview,
    discoverySearchOverview,
    errorPages,
    eventOverview,
    favoritePage,
    pressOverview,
    search,
    seriesOverview,
    site,
    translations,
    vriendenloterij,
    fetch: fetchGlobal,
    fetchNavigation,
    fetchSeo,
    fetchErrorPage,
  }
})
